import { noop } from 'underscore';
import React, { useContext, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useWcpConstent } from '@bingads-webui-campaign/global-app-init';
import { Constants } from '@bingads-webui-theme-2018/theme-fluent';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Link, Button } from '@fluentui/react-components';
import { useResponsiveSize } from '@adscore/with-responsive-size';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useIntersectionObserver } from '@bingads-webui-campaign-react/pmax-landing-utils';

import { getLandingPageFooterStyles } from './landing-page-footer-styles';

export function LandingPageFooter({ logger }) {
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getLandingPageFooterStyles);
  const displayMode = useResponsiveSize(Constants.ResponsiveSizeBreakpoints);
  const ref = useRef();
  const wcpConsent = useWcpConstent();
  const onScrollToView = useCallback(() => {
    logger.trace('LandingPageFooter scrolled into view');
  }, [logger]);

  useIntersectionObserver({
    ref,
    onScrollToView,
  });
  const year = (new Date()).getFullYear();
  const footerLogoText = `©  ${year} Microsoft`;

  const onContactUsClick = useCallback(() => {
    logger.trace('[Footer] Contact Us clicked');
  }, [logger]);

  const onPrivacyClick = useCallback(() => {
    logger.trace('[Footer] Privacy clicked');
  }, [logger]);

  const onLegalClick = useCallback(() => {
    logger.trace('[Footer] Legal clicked');
  }, [logger]);

  const onTradeMarkClick = useCallback(() => {
    logger.trace('[Footer] TradeMark clicked');
  }, [logger]);

  const onConsumerHealthPrivacyClick = useCallback(() => {
    logger.trace('[Footer] Consumer Health Privacy clicked');
  }, [logger]);

  const onManageCookies = useCallback(() => {
    if (wcpConsent.isConsentRequired) {
      wcpConsent.manageConsent();
    }
  }, [wcpConsent]);
  return (
    <div
      ref={ref}
      className={classNames(
        'landing-page-footer',
        classes.footerContainer,
        classes[`footerContainer-${displayMode}`]
      )}
      role="contentinfo"
    >
      <ul className={classes.footerContainerInner}>
        <li>
          <Link
            className={classNames(classes.linkContainer, classes[`linkContainer-${displayMode}`])}
            href="https://go.microsoft.com/fwlink?LinkID=398371"
            target="_blank"
            rel="noopener noreferrer"
            id="supportLabel"
            onClick={onContactUsClick}
          >
            {i18n.getString(_TL_('Contact Us'))}
          </Link>
        </li>
        <li>
          <Link
            className={classNames(classes.linkContainer, classes[`linkContainer-${displayMode}`])}
            href="https://go.microsoft.com/fwlink?LinkID=248681"
            target="_blank"
            rel="noopener noreferrer"
            id="privacyLabel"
            onClick={onPrivacyClick}
          >
            {i18n.getString(_TL_('Privacy & Cookies'))}
          </Link>
        </li>
        <li>
          <Link
            className={classNames(classes.linkContainer, classes[`linkContainer-${displayMode}`])}
            href="http://go.microsoft.com/fwlink/?LinkID=530144"
            target="_blank"
            rel="noopener noreferrer"
            id="legalLabel"
            onClick={onLegalClick}
          >
            {i18n.getString(_TL_('Legal'))}
          </Link>
        </li>
        <li>
          <Link
            className={classNames(classes.linkContainer, classes[`linkContainer-${displayMode}`])}
            href="https://go.microsoft.com/fwlink?LinkID=613809"
            target="_blank"
            rel="noopener noreferrer"
            id="tradeMarkLabel"
            onClick={onTradeMarkClick}
          >
            {i18n.getString(_TL_('Trademarks'))}
          </Link>
        </li>
        <li>
          <Link
            className={classNames(classes.linkContainer, classes[`linkContainer-${displayMode}`])}
            href="https://go.microsoft.com/fwlink/?linkid=2259814"
            target="_blank"
            rel="noopener noreferrer"
            id="consumerHealthPrivacyLabel"
            onClick={onConsumerHealthPrivacyClick}
          >
            {i18n.getString(_TL_('Consumer Health Privacy'))}
          </Link>
        </li>
        {wcpConsent.isConsentRequired &&
          <li className={classNames(classes.footerManageCookies)}>
            <Button
              onClick={onManageCookies}
              className={classNames(classes.linkContainer, classes[`linkContainer-${displayMode}`])}
            >
              {i18n.getString(_TL_('Manage Cookies'))}
            </Button>
          </li>
        }
        <li
          className="footer-nolink footer-logo"
          id="footer-logo"
        >
          {footerLogoText}
        </li>
      </ul>
    </div>
  );
}

LandingPageFooter.propTypes = {
  logger: PropTypes.shape({
    trace: PropTypes.func.isRequired,
  }),
};

LandingPageFooter.defaultProps = {
  logger: {
    trace: noop,
  },
};
